import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { api, type TaskEvent, type TranscriptEntry } from "./api";

/**
 * SSE plumbing. The server streams cursor-ordered rows (see apps/app
 * routes/stream.ts); EventSource handles reconnection itself and replays
 * `Last-Event-ID`, so these hooks never lose events — at worst they see one
 * twice, and every consumer dedupes on `seq`.
 */

/** The deployed vocabulary (states, transitions, models). Effectively static. */
export function useMeta() {
  return useQuery({
    queryKey: ["meta"],
    queryFn: api.meta,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * Global liveness: any task event invalidates the conversation list and the
 * touched conversation's detail — cheap, and the queries are all keyed.
 */
export function useGlobalStream(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    const es = new EventSource("/api/stream", { withCredentials: true });
    es.addEventListener("task_event", (e) => {
      const event = JSON.parse((e as MessageEvent).data) as TaskEvent;
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["task", event.taskId] });
      if (event.type === "state_changed") {
        queryClient.invalidateQueries({ queryKey: ["overview"] });
      }
    });
    return () => es.close();
  }, [queryClient]);
}

/**
 * Per-task stream: task events + transcript entries, handed to the caller as
 * they arrive. Handlers ride in refs so a re-render never resubscribes.
 */
export function useTaskStream(
  taskId: string | undefined,
  handlers: {
    onEvent?: (event: TaskEvent) => void;
    onTranscript?: (entry: TranscriptEntry) => void;
  },
): void {
  const ref = useRef(handlers);
  ref.current = handlers;

  useEffect(() => {
    if (!taskId) return;
    const es = new EventSource(`/api/tasks/${taskId}/stream`, {
      withCredentials: true,
    });
    es.addEventListener("task_event", (e) => {
      ref.current.onEvent?.(JSON.parse((e as MessageEvent).data) as TaskEvent);
    });
    es.addEventListener("transcript", (e) => {
      ref.current.onTranscript?.(
        JSON.parse((e as MessageEvent).data) as TranscriptEntry,
      );
    });
    return () => es.close();
  }, [taskId]);
}
